/**
 * Datums- und Zahlenformatierung - 1:1-Portierung aus `index.html` (Stand 2026-08-07, Zeilen wie
 * im Task-Brief). Die Rechenwege sind wörtlich aus dem Original übernommen, auch wo sie umständlich
 * wirken oder auf den ersten Blick fehlerhaft aussehen (siehe Bericht zu Task 2, Abschnitt
 * "Gefundene Abweichungen" für Details zu den auffälligsten Stellen). Typen wurden ergänzt, Namen
 * und Logik nicht angetastet - AUSSER `parseOrderListAmount`/`orderExcelNumber`: Befund 12,
 * Option B (entschieden 2026-08-11, Auftraggeber) ersetzt dort die einfache `.replace(',', '.')`
 * (nur das erste Komma) durch eine echte Tausendertrennzeichen-Erkennung, siehe
 * `kommaAlsDezimaltrenner` unten.
 *
 * Nicht portiert (laut Brief): `esc` (entfällt in React).
 */

// ---- Intl.NumberFormat-Instanzen (index.html:4545-4550) ----

export const EUR = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" });
export const EUR0 = new Intl.NumberFormat("de-DE", {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 0,
});
export const NUM = new Intl.NumberFormat("de-DE");
export const NUM4 = new Intl.NumberFormat("de-DE", {
  minimumFractionDigits: 4,
  maximumFractionDigits: 4,
});
/**
 * Immer genau zwei Nachkommastellen, de-DE (Komma statt Punkt) - neu fuer `fmtBytes()` unten
 * (Nachbesserung, Pruefer-Befund "Sollte" 5, 05.09.2026): `toFixed(2)` liefert einen Punkt
 * ("109.22 KB"), waehrend der Rest der Oberflaeche durchgaengig mit `NUM`/`NUM4`/`EKF` de-DE
 * formatiert (Komma). `NUM4` passt hier nicht (vier statt zwei Nachkommastellen), `EKF` ebenfalls
 * nicht (variable 2-4 Nachkommastellen) - beide sind fuer andere Groessenordnungen gedacht.
 */
export const NUM2 = new Intl.NumberFormat("de-DE", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
// Stückpreise (EK je Stk.) mit bis zu 4 Nachkommastellen – 2 Dezimalen sind bei Cent-Artikeln zu
// grob (index.html:4549-4550, Kommentar wörtlich übernommen).
export const EKF = new Intl.NumberFormat("de-DE", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
});

// ---- Datum (index.html:4772-4822) ----

/** index.html:4772 */
export function addDays(dateStr: string | null | undefined, days: unknown): string {
  if (days === "" || days === null || days === undefined || Number.isNaN(Number(days))) return "";
  const m = String(dateStr || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const d = m ? new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))) : new Date();
  d.setUTCDate(d.getUTCDate() + Number(days));
  return d.toISOString().slice(0, 10);
}

/** index.html:4779 */
export function etaBookingOffsetDays(orderOrValue: unknown = 14): number {
  const raw =
    typeof orderOrValue === "object" && orderOrValue !== null
      ? (orderOrValue as Record<string, unknown>).etaBookingOffsetDays
      : orderOrValue;
  const value = Number(raw);
  return Number.isFinite(value) ? Math.max(0, Math.min(365, Math.round(value))) : 14;
}

/** index.html:4786 */
export function estimatedBuchenDateFromEta(
  eta: string | null | undefined,
  offsetDays: unknown = 14,
): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(eta || ""))
    ? addDays(eta, etaBookingOffsetDays(offsetDays))
    : "";
}

/** index.html:4789 */
export function daysBetweenIsoDates(
  from: string | null | undefined,
  to: string | null | undefined,
): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(from || "")) || !/^\d{4}-\d{2}-\d{2}$/.test(String(to || "")))
    return null;
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86400000);
}

/** index.html:4798 */
export const todayIso = (): string => new Date().toISOString().slice(0, 10);

export interface IsoWeekInfo {
  key: string;
  year: number | string;
  week: number | string;
  start: string;
  end: string;
}

/** index.html:4799 */
export function isoWeekInfo(dateStr?: string | null): IsoWeekInfo {
  const date = new Date(`${dateStr || todayIso()}T12:00:00Z`);
  if (Number.isNaN(date.getTime())) return { key: "ohne", year: "", week: "", start: "", end: "" };
  const day = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  // Literal wie im Original eine Date-Subtraktion (dort `date - yearStart`); TypeScript erlaubt die
  // implizite Zahl-Koerzion zweier Date-Objekte nicht, `.getTime()` liefert denselben Zahlenwert wie
  // die von `valueOf()` erzwungene Original-Subtraktion - reine Typergänzung, keine Verhaltensänderung.
  const week = Math.ceil((((date.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  const monday = new Date(`${dateStr || todayIso()}T12:00:00Z`);
  const mondayDay = monday.getUTCDay() || 7;
  monday.setUTCDate(monday.getUTCDate() - mondayDay + 1);
  const sunday = new Date(monday);
  sunday.setUTCDate(sunday.getUTCDate() + 6);
  const year = date.getUTCFullYear();
  return {
    key: `${year}-W${String(week).padStart(2, "0")}`,
    year,
    week,
    start: monday.toISOString().slice(0, 10),
    end: sunday.toISOString().slice(0, 10),
  };
}

/** index.html:4820 */
export function daysFromToday(dateStr: string | null | undefined): number | null {
  if (!dateStr) return null;
  // Siehe Kommentar in isoWeekInfo: `.getTime()` ist eine reine Typergänzung fuer die im Original
  // implizite Date-Subtraktion (`new Date(dateStr) - new Date(todayIso())`).
  return Math.round((new Date(dateStr).getTime() - new Date(todayIso()).getTime()) / 864e5);
}

// ---- Textnormalisierung (index.html:6349) ----

/**
 * index.html:6349 - Normalisierung fuer Vergleiche und Suche (trimmen, kleinschreiben).
 *
 * Steht auf der Portierungsliste KEINES Tasks: fachlich gehoert `normSearch` zu den Filtern, die erst
 * spaeter an der Reihe sind. Gebraucht wird es aber schon jetzt von `lib/status.ts`
 * (`isSplitStatusOrder`) und `lib/payments.ts` (`inferPaymentMode`, `paymentsFromPiTerms`). Bis Task
 * 5a stand es modulintern in `lib/status.ts`; als `lib/payments.ts` hinzukam, waere daraus eine
 * zweite Fassung desselben Rechenwegs geworden. Ein Export aus `lib/status.ts` schied aus - dieses
 * Modul importiert seinerseits `isPaid`/`nextDue` aus `lib/payments.ts`, ein Import in die
 * Gegenrichtung waere ein Zyklus. `lib/format.ts` ist das Modul, das beide ohnehin importieren und
 * das selbst nichts aus `lib/` importiert; hier gibt es genau eine Fassung und keinen Zyklus. Der
 * Filter-Task importiert sie spaeter von hier, statt sie ein drittes Mal zu schreiben.
 */
export const normSearch = (v: unknown): string => String(v ?? "").trim().toLowerCase();

// ---- de-DE-Formatierung (index.html:4694-4695) ----

/** index.html:4694 */
export const fmtDate = (d: string | number | Date | null | undefined): string =>
  d ? new Date(d).toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" }) : "";

/** index.html:4695 */
export const fmtDT = (d: string | number | Date | null | undefined): string =>
  d
    ? new Date(d).toLocaleString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

/**
 * Der Kalendertag von `d` in der ORTSZEIT des Browsers, `YYYY-MM-DD` - NEU (Pruefer-Befund
 * "Wichtig" 1, Nachbesserung Historie-Tabelle, 2026-09-06). Gegenstueck zu einem blossen
 * `ts.slice(0, 10)` auf einem rohen ISO-UTC-Zeitstempel: das liefert den Tag in UTC, waehrend
 * `fmtDT` oben (und jede Stelle, die einen Zeitstempel anzeigt) die ORTSZEIT zeigt. Ein
 * Zeitraum-Filter, der stattdessen den UTC-String schneidet, blendet Zeilen anders aus/ein, als es
 * ihre sichtbare Zelle verspricht - Nachtjobs/Scheduler-Eintraege zwischen 22 und 24 Uhr UTC sind
 * der Regelfall (MESZ +2h ruft schon den naechsten Tag auf, im Winter MEZ +1h ab 23 Uhr).
 * `getFullYear`/`getMonth`/`getDate` sind LOKALE Getter (anders als die UTC-Varianten in `addDays`
 * oben) - genau das macht sie hier richtig, kein Datumsformat-Parsing per Hand.
 */
export function localIsoDay(d: string | number | Date | null | undefined): string {
  if (!d) return "";
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return "";
  const jahr = date.getFullYear();
  const monat = String(date.getMonth() + 1).padStart(2, "0");
  const tag = String(date.getDate()).padStart(2, "0");
  return `${jahr}-${monat}-${tag}`;
}

/**
 * Menschenlesbare Dateigroesse (Auftrag "DSV-Belege (Zustellbeleg + Rechnung) in der Bestellung
 * anzeigen") - KEIN Bestandsvorbild in `index.html`, neu fuer `Drawer/DsvBelege.tsx`. 1024er-
 * Schritte (KiB/MiB, wie ueberall sonst im Betrieb ueblich - die 15-MB-Grenze in `DokumenteTab.tsx`
 * rechnet ebenfalls mit `1024 * 1024`), zwei Nachkommastellen ab KB, Bytes selbst ganzzahlig ohne
 * Nachkommastellen. `NUM`/`NUM4` (oben) passen hier nicht: beide runden auf feste Nachkommastellen
 * UNABHAENGIG von der Groessenordnung, eine Datei mit 3 Bytes zeigte damit "3,0000 B" statt "3 B".
 * Werte oberhalb von TB werden weiterhin als (entsprechend grosse) TB-Zahl gezeigt - eine weitere
 * Einheit (PB) haette fuer die hier vorkommenden PDF-Groessen keinen praktischen Nutzen.
 *
 * KORRIGIERT (Pruefer-Befund "Sollte" 5, 05.09.2026): `toFixed(2)` lieferte einen Dezimalpunkt
 * ("109.22 KB") - der Rest der Oberflaeche formatiert Zahlen durchgaengig de-DE mit Komma (`NUM`/
 * `NUM4`/`EKF`). `NUM2` (oben) uebernimmt jetzt die Formatierung.
 */
export function fmtBytes(bytes: unknown): string {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return "";
  if (n < 1024) return `${Math.round(n)} B`;
  const einheiten = ["KB", "MB", "GB", "TB"];
  let wert = n / 1024;
  let index = 0;
  while (wert >= 1024 && index < einheiten.length - 1) {
    wert /= 1024;
    index++;
  }
  return `${NUM2.format(wert)} ${einheiten[index]}`;
}

// index.html:13019 - kein eigener Listeneintrag in Task 6, aber ein notwendiger Zulieferer von
// `fxWeekChartValue` (`src/lib/fx.ts`). Rein additiv, wie `normSearch` in Task 5a - siehe Bericht.
/**
 * index.html:13019. Der Cast ist reine Typarbeit: das Original ist ungetypt und multipliziert `v`
 * ungeprueft (`v * 100`) - JavaScript koerziert dabei jeden Wert ueber `ToNumber` (Zeichenketten,
 * `null`, `Date` ...), exakt wie ein `as number`-Cast zur Laufzeit nichts daran aendert.
 */
export function fmtUsd(v: unknown): string {
  return "$ " + NUM.format(Math.round((v as number) * 100) / 100);
}

// ---- Bestellliste / Excel-Import (index.html:7592-7608, 12805-12826) ----

/**
 * Befund 12, Option B (entschieden 2026-08-11, Auftraggeber): echte
 * Tausendertrennzeichen-Erkennung. Alle Kommas AUSSER dem letzten fallen weg, das letzte wird zum
 * Dezimalpunkt - so, wie der Beschluss es woertlich vorgibt. Das Original benutzt an dieser Stelle
 * `.replace(',', '.')` und ersetzt damit nur das ERSTE Komma; bei mehreren Kommas ohne Punkt
 * ("1,234,567") blieben die uebrigen stehen und `Number(...)` lieferte NaN.
 *
 * Ein globales `.replace(/,/g, '.')` waere KEINE gleichwertige Reparatur - daraus wuerde
 * "1.234.567", was als Ganzzahl mit Tausenderpunkten falsch gelesen wuerde.
 *
 * Bewusst NICHT exportiert - wie `orderExcelCell` unten: das Original kennt diese Funktion nicht,
 * ein Export braeuchte einen Eintrag im Herkunftsdokument und verschoebe die Zahl der Wert-Exporte.
 */
function kommaAlsDezimaltrenner(text: string): string {
  const letztes = text.lastIndexOf(",");
  if (letztes < 0) return text;
  return `${text.slice(0, letztes).replace(/,/g, "")}.${text.slice(letztes + 1)}`;
}

/** index.html:7592 */
export function parseOrderListAmount(value: unknown): number | null {
  const raw = String(value ?? "").trim();
  if (!raw) return null;
  const compact = raw.replace(/\s/g, "").replace(/[^\d,.-]/g, "");
  const comma = compact.lastIndexOf(",");
  const dot = compact.lastIndexOf(".");
  const normalized =
    comma > dot
      ? kommaAlsDezimaltrenner(compact.replace(/\./g, ""))
      : compact.replace(/,/g, "");
  const amount = Number(normalized);
  return Number.isFinite(amount) && amount >= 0 ? amount : NaN;
}

/** index.html:7605 */
export function orderListEditableAmount(value: unknown): string {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "";
  return amount.toLocaleString("de-DE", { useGrouping: false, maximumFractionDigits: 2 });
}

// index.html:12805-12807 - `parseOrderExcelMatrix` (lib/parsers/excel.ts) braucht orderExcelCell
// siebenmal, zusaetzlich zur Abhaengigkeit von orderExcelNumber hier direkt darunter. Eine zweite
// Fassung im Parser-Modul waere eine Dublette, die die Woertlichkeit unterlaeuft - deshalb bleibt
// die Funktion hier und wird exportiert (M4: die vierte Fundstelle der 80er-Grenze liegt ebenfalls
// in parseOrderExcelMatrix).
export function orderExcelCell(value: unknown): string {
  return String(value ?? "").replace(/\s+/g, " ").trim();
}

/** index.html:12809 */
export function orderExcelNumber(value: unknown): string {
  const source = orderExcelCell(value).replace(/[^0-9,.-]/g, "");
  if (!source || !/\d/.test(source)) return "";
  let normalized = source;
  const comma = normalized.lastIndexOf(",");
  const dot = normalized.lastIndexOf(".");
  if (comma >= 0 && dot >= 0) {
    normalized =
      comma > dot
        ? kommaAlsDezimaltrenner(normalized.replace(/\./g, ""))
        : normalized.replace(/,/g, "");
  } else if (comma >= 0) {
    const decimals = normalized.length - comma - 1;
    normalized =
      decimals > 0 && decimals <= 3
        ? kommaAlsDezimaltrenner(normalized.replace(/\./g, ""))
        : normalized.replace(/,/g, "");
  } else if ((normalized.match(/\./g) || []).length > 1) {
    const parts = normalized.split(".");
    const tail = parts.pop();
    normalized = parts.join("") + "." + tail;
  }
  const number = Number(normalized);
  return Number.isFinite(number) ? String(number) : "";
}

// ---- Dateinamen (index.html:4488-4492) ----

/** index.html:4488 */
export function safeLocalFsName(value: unknown): string {
  let name =
    String(value || "")
      .trim()
      .replace(/[<>:"/\\|?*\x00-\x1f]/g, "_")
      .replace(/[. ]+$/g, "")
      .slice(0, 120) || "Ohne_Name";
  if (/^(con|prn|aux|nul|com[1-9]|lpt[1-9])$/i.test(name)) name = "_" + name;
  return name;
}
